/**
 * MSI3 / AcuityDrill learner profile — client read of msi3LearnerProfiles/{uid}.
 * Writes go through saveMsi3LearnerProfile (Admin SDK). No US checkbox.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";

export const MSI3_LEARNER_PROFILES_COLLECTION = "msi3LearnerProfiles";

/**
 * @returns {{
 *   loading: boolean,
 *   profile: { learnerType: "student"|"clinician", orgId: string|null, onboardedAt?: unknown } | null,
 *   hasProfile: boolean,
 *   refresh: () => void,
 * }}
 */
export function useMsi3LearnerProfile() {
  const { user, userLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (userLoading) {
      setProfileLoading(true);
      return undefined;
    }

    if (!isRealAccount) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProfileLoading(true);

    const unsub = db
      .collection(MSI3_LEARNER_PROFILES_COLLECTION)
      .doc(uid)
      .onSnapshot(
        (snap) => {
          if (cancelled) return;
          setProfile(snap.exists ? snap.data() : null);
          setProfileLoading(false);
        },
        (err) => {
          console.warn("[useMsi3LearnerProfile] listen failed", err);
          if (cancelled) return;
          setProfile(null);
          setProfileLoading(false);
        },
      );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid, isRealAccount, userLoading, reloadToken]);

  const loading = Boolean(userLoading || (isRealAccount && profileLoading));
  const hasProfile = Boolean(!loading && profile);

  return {
    loading,
    profile: loading ? null : profile,
    hasProfile: loading ? false : hasProfile,
    refresh,
  };
}

export default useMsi3LearnerProfile;
